import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  Keyboard,
  Loader2,
  Search,
  Sparkles,
  Users,
  type LucideIcon,
} from "lucide-react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { Kbd } from "@/components/ui/kbd";
import { useRecordSearch, type RecordHit } from "@/hooks/use-record-search";
import { ASK_ADVA_LABEL, useActions, type ActionDef } from "@/lib/actions";
import { ESCAPE_PRIORITY, registerEscapeHandler } from "@/lib/escape-stack";
import { SCREENS, type ScreenDef } from "@/lib/screens";
import { getShortcut } from "@/lib/shortcuts";

/**
 * RecordSearchResults — palette section that fans out across all five
 * `search`-capable entities (customer, contact, opportunity, product, crew)
 * and renders one CommandItem per hit. Extracted so the TanStack
 * `useRecordSearch` hook (which requires a `QueryClientProvider`) only
 * mounts when the palette is open AND the debounced query is at least
 * two characters.
 */
function RecordSearchResults({
  query,
  onSelect,
}: {
  query: string;
  onSelect: (hit: RecordHit) => void;
}) {
  const { results, isLoading, isError } = useRecordSearch(query);

  return (
    <CommandGroup heading="Records">
      {isLoading && (
        <div className="flex items-center justify-center py-3 text-xs text-muted-foreground">
          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
          Searching…
        </div>
      )}
      {!isLoading && results.length === 0 && isError && (
        <div className="py-3 text-center text-xs text-destructive">
          Search is temporarily unavailable.
        </div>
      )}
      {!isLoading && results.length === 0 && !isError && (
        <div className="py-3 text-center text-xs text-muted-foreground">
          No records match "{query}".
        </div>
      )}
      {results.map((hit) => (
        <CommandItem
          key={hit.id}
          // Append the active query to the value so cmdk's fuzzy filter
          // never hides a row the API has already matched. The label
          // remains the primary user-visible affordance.
          value={`${hit.entity} ${hit.label} ${hit.sublabel ?? ""} ${query}`}
          onSelect={() => onSelect(hit)}
        >
          <Users className="text-muted-foreground" />
          <div className="flex flex-1 flex-col">
            <span className="text-sm">{hit.label}</span>
            {hit.sublabel && (
              <span className="text-xs text-muted-foreground">
                {hit.sublabel}
              </span>
            )}
          </div>
        </CommandItem>
      ))}
    </CommandGroup>
  );
}

const RECENT_KEY = "adva:cmd-palette:recent";
const RECENT_LIMIT = 5;

type RecentEntry = {
  id: string;
  label: string;
  href: string;
};

function loadRecent(): RecentEntry[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (e): e is RecentEntry =>
          !!e &&
          typeof (e as RecentEntry).id === "string" &&
          typeof (e as RecentEntry).label === "string" &&
          typeof (e as RecentEntry).href === "string",
      )
      .slice(0, RECENT_LIMIT);
  } catch (err) {
    console.warn("cmd-palette.recent.parse-failed", err);
    return [];
  }
}

function saveRecent(entries: RecentEntry[]) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(
      RECENT_KEY,
      JSON.stringify(entries.slice(0, RECENT_LIMIT)),
    );
  } catch (err) {
    // QuotaExceededError is expected; SecurityError (private mode) /
    // unknown-failure paths are not — log at debug to tell them apart.
    const name = err instanceof Error ? err.name : "Unknown";
    if (name !== "QuotaExceededError") {
      console.warn(`cmd-palette.recent.save-failed (${name})`, err);
    }
  }
}

const ACTION_GROUP_HEADINGS: Record<ActionDef["group"], string> = {
  appearance: "Appearance",
  account: "Account",
  navigation: "Actions",
  ai: "Ask Adva",
};

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Open the keyboard shortcuts help panel from the palette. */
  onOpenShortcutsHelp?: () => void;
}

/**
 * Global ⌘K palette — searches records, screens, and actions, plus an
 * Ask Adva entry. Sections render in order:
 *   1. Recently used (localStorage — empty-query only)
 *   2. Records   (multi-entity search via useRecordSearch — query ≥ 2 chars)
 *   3. Screens   (canonical SCREENS registry)
 *   4. Actions   (useActions — appearance + account + navigation groups)
 *   5. Ask Adva  (useActions ai group — always visible)
 *   6. Global    (search hint + close — existing affordances)
 *
 * The outer component is intentionally lightweight: it only mounts the
 * CommandDialog shell and the Escape-stack registration. All hook-heavy
 * work (useActions → useAuth/useTheme/useBusinesses; useRecordSearch →
 * five TanStack queries) lives in `CommandPaletteContent`, which only
 * renders when `open === true`. This keeps callers that mount the palette
 * shell (e.g. KeyboardShortcutsProvider in test layouts) from being forced
 * to provide AuthProvider/QueryClientProvider until the palette is opened.
 */
export function CommandPalette({
  open,
  onOpenChange,
  onOpenShortcutsHelp,
}: CommandPaletteProps) {
  // Keep a stable ref so the Escape handler effect doesn't re-register on
  // every render.
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  // Wire the palette into the global Escape stack at modal priority. cmdk
  // already closes on Escape internally, but we register here so the palette
  // counts as one frame in the user-visible Escape hierarchy.
  useEffect(() => {
    if (!open) return;
    return registerEscapeHandler(() => {
      onOpenChangeRef.current(false);
      return true;
    }, ESCAPE_PRIORITY.modal);
  }, [open]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <CommandPaletteContent
          onOpenChange={onOpenChange}
          onOpenShortcutsHelp={onOpenShortcutsHelp}
        />
      )}
    </CommandDialog>
  );
}

function CommandPaletteContent({
  onOpenChange,
  onOpenShortcutsHelp,
}: {
  onOpenChange: (open: boolean) => void;
  onOpenShortcutsHelp?: () => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [recent, setRecent] = useState<RecentEntry[]>(() => loadRecent());

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  const actions = useActions({ onOpenShortcutsHelp });

  const actionsByGroup = useMemo(() => {
    const groups: Record<ActionDef["group"], ActionDef[]> = {
      appearance: [],
      account: [],
      navigation: [],
      ai: [],
    };
    for (const a of actions) groups[a.group].push(a);
    return groups;
  }, [actions]);

  const recordRecent = useCallback((entry: RecentEntry) => {
    setRecent((prev) => {
      const next = [entry, ...prev.filter((e) => e.id !== entry.id)].slice(
        0,
        RECENT_LIMIT,
      );
      saveRecent(next);
      return next;
    });
  }, []);

  const runScreen = useCallback(
    (screen: ScreenDef) => {
      recordRecent({ id: screen.id, label: screen.label, href: screen.href });
      onOpenChange(false);
      navigate(screen.href);
    },
    [recordRecent, navigate, onOpenChange],
  );

  const runRecord = useCallback(
    (hit: RecordHit) => {
      recordRecent({ id: hit.id, label: hit.label, href: hit.href });
      onOpenChange(false);
      navigate(hit.href);
    },
    [recordRecent, navigate, onOpenChange],
  );

  const runRecent = useCallback(
    (entry: RecentEntry) => {
      recordRecent(entry);
      onOpenChange(false);
      navigate(entry.href);
    },
    [recordRecent, navigate, onOpenChange],
  );

  const runAction = useCallback(
    (action: ActionDef) => {
      onOpenChange(false);
      action.run();
    },
    [onOpenChange],
  );

  const cmdK = getShortcut("openCommand");
  const helpShortcut = getShortcut("openShortcutsHelp");
  const searchShortcut = getShortcut("openSearch");

  const showSearchResults = debouncedQuery.length >= 2;

  function renderActionItem(action: ActionDef) {
    const Icon: LucideIcon = action.icon;
    return (
      <CommandItem
        key={action.id}
        value={`${action.label} ${(action.keywords ?? []).join(" ")}`}
        onSelect={() => runAction(action)}
      >
        <Icon className="text-muted-foreground" />
        <span>{action.label}</span>
      </CommandItem>
    );
  }

  return (
    <>
      <CommandInput
        placeholder="Type a command, search records, or jump to a page…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {!query && recent.length > 0 && (
          <CommandGroup heading="Recently used">
            {recent.map((entry) => (
              <CommandItem
                key={entry.id}
                value={`recent ${entry.label}`}
                onSelect={() => runRecent(entry)}
              >
                <CalendarDays className="text-muted-foreground" />
                <span>{entry.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {showSearchResults && (
          <>
            <RecordSearchResults query={debouncedQuery} onSelect={runRecord} />
            <CommandSeparator />
          </>
        )}

        <CommandGroup heading="Screens">
          {SCREENS.map((screen) => {
            const Icon = screen.icon as LucideIcon;
            return (
              <CommandItem
                key={screen.id}
                value={`${screen.label} ${(screen.keywords ?? []).join(" ")}`}
                onSelect={() => runScreen(screen)}
              >
                <Icon className="text-muted-foreground" />
                <span>{screen.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>

        {actionsByGroup.appearance.length > 0 && (
          <CommandGroup heading={ACTION_GROUP_HEADINGS.appearance}>
            {actionsByGroup.appearance.map(renderActionItem)}
          </CommandGroup>
        )}
        {actionsByGroup.account.length > 0 && (
          <CommandGroup heading={ACTION_GROUP_HEADINGS.account}>
            {actionsByGroup.account.map(renderActionItem)}
          </CommandGroup>
        )}
        {actionsByGroup.navigation.length > 0 && (
          <CommandGroup heading={ACTION_GROUP_HEADINGS.navigation}>
            {actionsByGroup.navigation.map(renderActionItem)}
          </CommandGroup>
        )}
        {actionsByGroup.ai.length > 0 && (
          <CommandGroup heading={ASK_ADVA_LABEL}>
            {actionsByGroup.ai.map((action) => (
              <CommandItem
                key={action.id}
                value={`${action.label} ${(action.keywords ?? []).join(" ")}`}
                onSelect={() => runAction(action)}
              >
                <Sparkles className="text-muted-foreground" />
                <span>{action.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandSeparator />

        <CommandGroup heading="Global">
          {searchShortcut && (
            <CommandItem
              value="search find records"
              onSelect={() => {
                setQuery("/");
              }}
            >
              <Search className="text-muted-foreground" />
              <span>Search records</span>
              <CommandShortcut>
                <Kbd>{searchShortcut.compact}</Kbd>
              </CommandShortcut>
            </CommandItem>
          )}
          {helpShortcut && onOpenShortcutsHelp && (
            <CommandItem
              value="keyboard shortcuts help"
              onSelect={() => {
                onOpenChange(false);
                onOpenShortcutsHelp();
              }}
            >
              <Keyboard className="text-muted-foreground" />
              <span>Keyboard shortcuts</span>
              <CommandShortcut>
                <Kbd>{helpShortcut.compact}</Kbd>
              </CommandShortcut>
            </CommandItem>
          )}
          {cmdK && (
            <CommandItem
              value="command palette toggle"
              onSelect={() => onOpenChange(false)}
            >
              <Search className="text-muted-foreground" />
              <span>Close palette</span>
              <CommandShortcut>
                <Kbd>{cmdK.compact}</Kbd>
              </CommandShortcut>
            </CommandItem>
          )}
        </CommandGroup>
      </CommandList>
    </>
  );
}
